import React, { forwardRef } from "react";
import { Autocomplete, TextField, InputAdornment, Box } from "@mui/material";
import { OptionProps } from "../../constants/types";

type MuiSearchSelectProps = {
  label?: string;
  placeholder: string;
  options: OptionProps[];
  isInvalid?: boolean;
  errorText?: string;
  isRequired?: boolean;
  onChange: (value: string, id: string, data: any) => void;
  onBlur?: () => void;
  value: string;
  inputValue?: string;
  onInputChange?: (input: string) => void;
  iconStart?: React.ReactNode;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

const MuiSearchSelect = forwardRef<HTMLInputElement, MuiSearchSelectProps>(
  (
    {
      onChange,
      options,
      placeholder,
      value,
      errorText,
      isInvalid = false,
      isRequired = false,
      label,
      onBlur,
      inputValue,
      onInputChange,
      iconStart,
      disabled,
    },
    ref
  ) => {
    // Label with red asterisk
    const renderLabel =
      isRequired && label ? (
        <span>
          {label}
          <span style={{ color: "red" }}> *</span>
        </span>
      ) : (
        label
      );

    return (
      <Autocomplete
        id="search-select"
        options={options}
        autoHighlight
        value={options.find((opt) => opt.value === value) || null}
        inputValue={inputValue}
        onBlur={onBlur}
        disabled={disabled}
        onChange={(_, selectedOption) => {
          if (selectedOption) {
            onChange(selectedOption.value, selectedOption.id, selectedOption);
          } else {
            // Reset to initial
            onChange("", "", null);
          }
        }}
        onInputChange={(_, newInputValue) => {
          if (onInputChange) onInputChange(newInputValue);
        }}
        getOptionLabel={(option) => option.label}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            {option.label}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={renderLabel}
            placeholder={placeholder}
            error={isInvalid}
            helperText={isInvalid ? errorText : ""}
            inputRef={ref}
            InputProps={{
              ...params.InputProps,
              startAdornment: iconStart ? (
                <InputAdornment position="start">{iconStart}</InputAdornment>
              ) : null,
            }}
          />
        )}
      />
    );
  }
);

export default MuiSearchSelect;
